import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ImageIcon from '@mui/icons-material/Image';
import VideocamIcon from '@mui/icons-material/Videocam';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import SearchIcon from '@mui/icons-material/Search';
import UploadIcon from '@mui/icons-material/CloudUpload';
import LibraryAddIcon from '@mui/icons-material/LibraryAdd';
import {
  ConnectorLibraryMedia,
  ConnectorLibraryMediaType,
  blockDemoMutation,
  deleteLibraryMedia,
  fetchMediaLibrary,
  isDemoMode,
  uploadLibraryMedia,
} from '../services/api';
import MediaOpensDialog from '../components/MediaOpensDialog';
import ProjectEssentialMediaPicker from '../components/ProjectEssentialMediaPicker';
import {
  MEDIA_UPLOAD_MAX_BYTES,
  VOICE_RECORDING_MAX_BYTES,
  asTypedUploadBlob,
  formatUploadSize,
  libraryUploadIssue,
  recordingTooLargeMessage,
} from '../utils/mediaUploadLimits';

const TYPE_OPTIONS: { value: '' | ConnectorLibraryMediaType; label: string }[] = [
  { value: '', label: 'All types' },
  { value: 'image', label: 'Images' },
  { value: 'video', label: 'Videos' },
  { value: 'document', label: 'PDFs' },
  { value: 'voice', label: 'Voice notes' },
];

const TYPE_VISUAL: Record<string, { color: string; Icon: typeof ImageIcon; label: string }> = {
  image: { color: '#2E7D32', Icon: ImageIcon, label: 'Image' },
  video: { color: '#3949AB', Icon: VideocamIcon, label: 'Video' },
  document: { color: '#D32F2F', Icon: PictureAsPdfIcon, label: 'PDF' },
  voice: { color: '#1565C0', Icon: MicIcon, label: 'Voice' },
};

function MediaTypeTile({ mediaType }: { mediaType: string }) {
  const kind = TYPE_VISUAL[mediaType] || TYPE_VISUAL.document;
  const { color, Icon, label } = kind;
  return (
    <Box
      aria-hidden
      sx={{
        width: '100%',
        height: { xs: 120, sm: 140 },
        borderRadius: 1,
        border: `1px solid ${color}33`,
        bgcolor: `${color}14`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.75,
      }}
    >
      <Icon sx={{ color, fontSize: { xs: 36, sm: 40 } }} />
      <Typography variant="caption" fontWeight={700} sx={{ color, letterSpacing: 0.4 }}>
        {label}
      </Typography>
    </Box>
  );
}

function MediaPreview({ item }: { item: ConnectorLibraryMedia }) {
  if (item.mediaType === 'image' && item.url) {
    return (
      <Box
        sx={{
          width: '100%',
          height: { xs: 120, sm: 140 },
          borderRadius: 1,
          bgcolor: 'var(--color-bg)',
          border: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          component="img"
          src={item.url}
          alt={item.name}
          sx={{
            maxWidth: '100%',
            maxHeight: '100%',
            width: 'auto',
            height: 'auto',
            objectFit: 'contain',
          }}
        />
      </Box>
    );
  }
  if (item.mediaType === 'video' && item.url) {
    return (
      <Box
        sx={{
          width: '100%',
          height: { xs: 120, sm: 140 },
          borderRadius: 1,
          bgcolor: '#000',
          overflow: 'hidden',
          border: '1px solid var(--color-border)',
        }}
      >
        <Box
          component="video"
          src={item.url}
          muted
          preload="metadata"
          sx={{ width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </Box>
    );
  }
  return <MediaTypeTile mediaType={item.mediaType} />;
}

function acceptForType(t: ConnectorLibraryMediaType): string {
  if (t === 'image') return 'image/*';
  if (t === 'video') return 'video/*';
  if (t === 'document') return 'application/pdf';
  return 'audio/*';
}

function fileToDataUrl(file: File | Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

function formatElapsed(ms: number) {
  const s = Math.floor(ms / 1000);
  return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
}

/**
 * Record a voice note locally; upload only when the user clicks Upload.
 * Prefers Opus containers so WhatsApp can show a PTT voice-note bubble.
 */
function VoiceNoteCapture({
  busy,
  onError,
  onUploaded,
}: {
  busy: boolean;
  onError: (message: string | null) => void;
  onUploaded: () => Promise<void>;
}) {
  const [recording, setRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [blob, setBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startRef = useRef(0);
  const previewUrlRef = useRef<string | null>(null);

  const clearPreview = useCallback(() => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
    setBlob(null);
  }, []);

  useEffect(() => () => {
    if (timerRef.current) clearInterval(timerRef.current);
    mediaRecorderRef.current?.stream.getTracks().forEach((t) => t.stop());
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const start = async () => {
    onError(null);
    clearPreview();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const preferredTypes = [
        'audio/ogg;codecs=opus',
        'audio/webm;codecs=opus',
        'audio/ogg',
        'audio/webm',
        'audio/mp4;codecs=mp4a.40.2',
        'audio/mp4',
      ];
      const pickedType = preferredTypes.find(
        (t) => typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.(t),
      );
      const mr = pickedType ? new MediaRecorder(stream, { mimeType: pickedType }) : new MediaRecorder(stream);
      mediaRecorderRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const rawType = mr.mimeType || pickedType || 'audio/webm';
        const baseType = rawType.split(';')[0].trim();
        const next = new Blob(chunksRef.current, { type: baseType });
        stream.getTracks().forEach((t) => t.stop());
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        if (next.size > VOICE_RECORDING_MAX_BYTES) {
          onError(recordingTooLargeMessage(next.size));
          return;
        }
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
        const url = URL.createObjectURL(next);
        previewUrlRef.current = url;
        setBlob(next);
        setPreviewUrl(url);
      };
      mr.start();
      startRef.current = Date.now();
      setElapsedMs(0);
      timerRef.current = setInterval(() => setElapsedMs(Date.now() - startRef.current), 250);
      setRecording(true);
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Microphone unavailable');
    }
  };

  const stop = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const upload = async () => {
    if (!blob) return;
    setUploading(true);
    onError(null);
    try {
      const dataUrl = await fileToDataUrl(blob);
      await uploadLibraryMedia({
        dataUrl,
        mediaType: 'voice',
        name: 'Voice note',
      });
      clearPreview();
      await onUploaded();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const disabled = busy || uploading;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 1.25,
        alignItems: { xs: 'stretch', sm: 'center' },
        flexWrap: 'wrap',
        minWidth: 0,
      }}
    >
      {!recording ? (
        <Button
          variant="outlined"
          size="small"
          disabled={disabled}
          startIcon={<MicIcon />}
          onClick={() => { void start(); }}
          sx={{ textTransform: 'none', fontWeight: 700, flexShrink: 0 }}
        >
          {blob ? 'Re-record' : 'Record voice'}
        </Button>
      ) : (
        <Button
          variant="contained"
          size="small"
          color="error"
          startIcon={<StopIcon />}
          onClick={stop}
          sx={{ textTransform: 'none', fontWeight: 700, flexShrink: 0 }}
        >
          Stop ({formatElapsed(elapsedMs)})
        </Button>
      )}
      {previewUrl && !recording && (
        <audio src={previewUrl} controls style={{ height: 32, maxWidth: 220 }} />
      )}
      <Button
        variant="contained"
        disabled={disabled || !blob || recording}
        startIcon={uploading ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
        onClick={() => { void upload(); }}
        sx={{
          bgcolor: '#ed8936',
          '&:hover': { bgcolor: '#dd7826' },
          textTransform: 'none',
          fontWeight: 700,
          px: 2.5,
          py: 1,
          boxShadow: '0 2px 8px rgba(237,137,54,0.35)',
          width: { xs: '100%', sm: 'auto' },
          flexShrink: 0,
        }}
      >
        {uploading ? 'Uploading…' : 'Upload'}
      </Button>
    </Box>
  );
}

export default function MediaLibrary() {
  const [items, setItems] = useState<ConnectorLibraryMedia[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState<'' | ConnectorLibraryMediaType>('');
  const [q, setQ] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [uploadType, setUploadType] = useState<ConnectorLibraryMediaType>('image');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [opensItem, setOpensItem] = useState<ConnectorLibraryMedia | null>(null);
  const [peImportOpen, setPeImportOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchMediaLibrary({
        type: type || undefined,
        q: q || undefined,
        limit: 100,
        offset: 0,
      });
      setItems(result.items);
      setTotal(result.total);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load media');
    } finally {
      setLoading(false);
    }
  }, [type, q]);

  useEffect(() => { void load(); }, [load]);

  const showDemoBlocked = () => {
    try {
      blockDemoMutation();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'This is a demo. Register your interest to unlock full access.');
    }
  };


  const handleUpload = async (file: File | null) => {
    if (!file) return;
    const issue = libraryUploadIssue(file, uploadType);
    if (issue) {
      setError(issue);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const dataUrl = await fileToDataUrl(asTypedUploadBlob(file));
      await uploadLibraryMedia({
        dataUrl,
        mediaType: uploadType,
        name: file.name.replace(/\.[^.]+$/, '') || file.name,
      });
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (item: ConnectorLibraryMedia) => {
    if (!item.canDelete) return;
    if (!window.confirm(`Delete “${item.name}”? This cannot be undone.`)) return;
    setBusy(true);
    setError(null);
    try {
      await deleteLibraryMedia(item.id);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const openOpensDialog = (item: ConnectorLibraryMedia) => {
    setOpensItem(item);
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 1 }}>Media</Typography>
      <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)', mb: 2 }}>
        Upload images, videos, PDFs, and voice notes for campaigns. Files that have already been sent cannot be deleted.
      </Typography>

      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 1.5,
        mb: 2,
        alignItems: { md: 'center' },
        justifyContent: { md: 'space-between' },
      }}>
        {/* Filters */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.25,
          alignItems: { xs: 'stretch', sm: 'center' },
          minWidth: 0,
        }}>
          <FormControl size="small" sx={{ width: { xs: '100%', sm: 150 }, flexShrink: 0 }}>
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={type}
              onChange={(e) => setType(e.target.value as '' | ConnectorLibraryMediaType)}
            >
              {TYPE_OPTIONS.map((o) => (
                <MenuItem key={o.value || 'all'} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{
            display: 'flex',
            gap: 1,
            alignItems: 'center',
            width: { xs: '100%', sm: 'auto' },
          }}>
            <TextField
              size="small"
              placeholder="Search by name"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') setQ(searchInput.trim()); }}
              InputProps={{
                startAdornment: <SearchIcon sx={{ fontSize: 18, color: 'var(--color-text-faint)', mr: 0.75 }} />,
              }}
              sx={{
                width: { xs: '100%', sm: 220 },
                maxWidth: { xs: 'none', sm: 220 },
                flex: { xs: 1, sm: '0 0 auto' },
              }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() => setQ(searchInput.trim())}
              sx={{ textTransform: 'none', flexShrink: 0, px: { xs: 1.5, sm: 2 } }}
            >
              Search
            </Button>
          </Box>
        </Box>

        {/* Upload / import */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1.25,
          alignItems: { xs: 'stretch', sm: 'center' },
          flexShrink: 0,
        }}>
          <Button
            variant="outlined"
            disabled={busy}
            startIcon={<LibraryAddIcon />}
            onClick={() => {
              if (isDemoMode()) {
                showDemoBlocked();
                return;
              }
              setPeImportOpen(true);
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderColor: '#ed8936',
              color: '#ed8936',
              '&:hover': { borderColor: '#dd7826', bgcolor: 'rgba(237,137,54,0.08)' },
              width: { xs: '100%', sm: 'auto' },
              flexShrink: 0,
            }}
          >
            Import from Project Essential
          </Button>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1.25,
              alignItems: { xs: 'stretch', sm: 'center' },
              flexShrink: 0,
              px: 1.25,
              pt: 1.75,
              pb: 1.25,
              borderRadius: 1.5,
              border: '1px solid rgba(237,137,54,0.35)',
              bgcolor: 'rgba(237,137,54,0.06)',
              overflow: 'visible',
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: { xs: 'block', sm: 'none' },
                fontWeight: 700,
                color: '#ed8936',
                letterSpacing: 0.3,
                textTransform: 'uppercase',
                fontSize: '0.65rem',
                mt: { xs: -0.5, sm: 0 },
              }}
            >
              Upload
            </Typography>
            <FormControl size="small" sx={{ width: { xs: '100%', sm: 140 }, flexShrink: 0 }}>
              <InputLabel id="media-library-upload-as-label">Upload as</InputLabel>
              <Select
                labelId="media-library-upload-as-label"
                label="Upload as"
                value={uploadType}
                onChange={(e) => setUploadType(e.target.value as ConnectorLibraryMediaType)}
              >
                <MenuItem value="image">Image</MenuItem>
                <MenuItem value="video">Video</MenuItem>
                <MenuItem value="document">PDF</MenuItem>
                <MenuItem value="voice">Voice</MenuItem>
              </Select>
            </FormControl>
            {uploadType === 'voice' ? (
              <VoiceNoteCapture
                busy={busy}
                onError={setError}
                onUploaded={load}
              />
            ) : (
              <Button
                component={isDemoMode() ? 'button' : 'label'}
                variant="contained"
                disabled={busy}
                startIcon={busy ? <CircularProgress size={18} color="inherit" /> : <UploadIcon />}
                onClick={() => {
                  if (isDemoMode()) showDemoBlocked();
                }}
                sx={{
                  bgcolor: '#ed8936',
                  '&:hover': { bgcolor: '#dd7826' },
                  textTransform: 'none',
                  fontWeight: 700,
                  px: 2.5,
                  py: 1,
                  boxShadow: '0 2px 8px rgba(237,137,54,0.35)',
                  width: { xs: '100%', sm: 'auto' },
                  flexShrink: 0,
                }}
              >
                Upload file
                {!isDemoMode() && (
                  <input
                    hidden
                    type="file"
                    accept={acceptForType(uploadType)}
                    onChange={(e) => {
                      const f = e.target.files?.[0] || null;
                      e.target.value = '';
                      void handleUpload(f);
                    }}
                  />
                )}
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      <Typography variant="caption" sx={{ display: 'block', color: 'var(--color-text-faint)', mt: -1, mb: 2, textAlign: { md: 'right' } }}>
        Uploads are capped at {formatUploadSize(MEDIA_UPLOAD_MAX_BYTES)} per image, video or PDF, and{' '}
        {formatUploadSize(VOICE_RECORDING_MAX_BYTES)} per voice note.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : items.length === 0 ? (
        <Typography sx={{ color: 'var(--color-text-faint)', textAlign: 'center', py: 6 }}>
          No media yet — upload a file to get started.
        </Typography>
      ) : (
        <>
          <Typography variant="caption" sx={{ color: 'var(--color-text-faint)', display: 'block', mb: 1 }}>
            {total} file{total === 1 ? '' : 's'}
          </Typography>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' },
            gap: { xs: 1.25, sm: 1.5 },
          }}>
            {items.map((item) => {
              const kind = TYPE_VISUAL[item.mediaType] || TYPE_VISUAL.document;
              const KindIcon = kind.Icon;
              return (
                <Card key={item.id} sx={{ bgcolor: 'var(--color-surface)', minWidth: 0 }}>
                  <CardActionArea
                    onClick={() => openOpensDialog(item)}
                    sx={{
                      alignItems: 'stretch',
                      height: '100%',
                      '& .MuiCardActionArea-focusHighlight': { opacity: 0.04 },
                    }}
                  >
                    <CardContent sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      p: { xs: 1.25, sm: 2 },
                      '&:last-child': { pb: { xs: 1.25, sm: 2 } },
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, minWidth: 0 }}>
                        <KindIcon sx={{ color: kind.color, fontSize: 20, mt: 0.25, flexShrink: 0 }} />
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            title={item.name}
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.name}
                          </Typography>
                          <Chip
                            size="small"
                            label={kind.label}
                            sx={{ height: 18, fontSize: '0.65rem', mt: 0.5, mr: 0.5 }}
                          />
                          {item.sourceEdmId ? (
                            <Chip
                              size="small"
                              label={item.sourceUnavailable ? 'Unavailable in PE' : 'Project Essential'}
                              sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                mt: 0.5,
                                bgcolor: item.sourceUnavailable ? 'rgba(220,38,38,0.15)' : 'rgba(48,86,211,0.12)',
                                color: item.sourceUnavailable ? '#DC2626' : '#3056D3',
                                fontWeight: 700,
                              }}
                            />
                          ) : null}
                        </Box>
                        <Tooltip title={item.canDelete ? 'Delete' : 'Already sent — cannot delete'}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={!item.canDelete || busy}
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                void handleDelete(item);
                              }}
                              sx={{ color: item.canDelete ? 'var(--color-text-subtle)' : 'var(--color-text-faint)', flexShrink: 0 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      <MediaPreview item={item} />
                      <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', sm: 'row' },
                        gap: { xs: 0.25, sm: 1 },
                        alignItems: { sm: 'baseline' },
                        flexWrap: 'wrap',
                      }}>
                        <Typography variant="caption" sx={{ color: 'var(--color-text-subtle)' }}>
                          {item.openCount} open{item.openCount === 1 ? '' : 's'}
                          {item.uniqueClientCount > 0 ? ` · ${item.uniqueClientCount} client${item.uniqueClientCount === 1 ? '' : 's'}` : ''}
                          {' · '}
                          <Box component="span" sx={{ color: '#ed8936' }}>View opens</Box>
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'var(--color-text-faint)' }}>
                          {new Date(item.createdAt).toLocaleString()}
                          {item.firstSentAt ? ' · Sent' : ''}
                        </Typography>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              );
            })}
          </Box>
        </>
      )}

      <MediaOpensDialog
        open={!!opensItem}
        media={opensItem}
        onClose={() => setOpensItem(null)}
      />

      <Dialog
        open={peImportOpen}
        onClose={() => setPeImportOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: 'var(--color-surface)', color: 'var(--color-text-strong)' } }}
      >
        <DialogTitle>Import from Project Essential</DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Typography variant="body2" sx={{ color: 'var(--color-text-subtle)', mb: 1.5 }}>
            Search EDMs and eBooks, then save a linked copy to your Media library. Files stay in sync with Project Essential when you send or clients open them.
          </Typography>
          <ProjectEssentialMediaPicker
            onImported={async () => {
              setPeImportOpen(false);
              await load();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPeImportOpen(false)} sx={{ textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
